import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BiClipboard, BiUserCircle, BiBuildings, BiBox, BiCog,
  BiBarChartAlt2, BiTrash, BiLogOut, BiChevronLeft, BiChevronRight, BiMenu,
} from 'react-icons/bi';
import { updatePassword } from 'firebase/auth';
import { auth } from '../../firebaseInstance';
import swal from 'sweetalert2';
import axios from 'axios';

const EXPANDED_W = 230;
const COLLAPSED_W = 68;
const MOBILE_BP = '768px';

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BP}) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 99;
    opacity: ${({ $open }) => $open ? 1 : 0};
    pointer-events: ${({ $open }) => $open ? 'auto' : 'none'};
    transition: opacity 0.2s ease;
  }
`;

const HamburgerBtn = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BP}) {
    display: flex;
    width: 36px;
    height: 36px;
    background: #1c1c1e;
    border-radius: 10px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 101;
    color: #f5f5f7;
    font-size: 20px;
    flex-shrink: 0;
  }
`;

const Container = styled.div`
  z-index: 100;
  height: 94vh;
  width: ${({ $collapsed }) => $collapsed ? COLLAPSED_W : EXPANDED_W}px;
  min-width: ${({ $collapsed }) => $collapsed ? COLLAPSED_W : EXPANDED_W}px;
  margin: 16px;
  padding: ${({ $collapsed }) => $collapsed ? '20px 8px' : '20px 14px'};
  background: #1c1c1e;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease, min-width 0.2s ease, padding 0.2s ease, transform 0.25s ease;
  position: relative;

  @media (max-width: ${MOBILE_BP}) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    margin: 0;
    border-radius: 0 20px 20px 0;
    width: ${EXPANDED_W}px;
    min-width: ${EXPANDED_W}px;
    padding: 20px 14px;
    transform: translateX(${({ $mobileOpen }) => $mobileOpen ? '0' : '-100%'});
    z-index: 100;
  }
`;

const ToggleBtn = styled.div`
  position: absolute;
  top: 24px;
  right: -12px;
  width: 24px;
  height: 24px;
  background: #2c2c2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  transition: all 0.15s;
  z-index: 10;
  &:hover {
    background: #3a3a3c;
    color: #fff;
  }
  @media (max-width: ${MOBILE_BP}) {
    display: none;
  }
`;

const Logo = styled.div`
  padding: ${({ $collapsed }) => $collapsed ? '4px 0 16px' : '4px 12px 16px'};
  font-size: ${({ $collapsed }) => $collapsed ? '13px' : '16px'};
  font-weight: 800;
  color: #f5f5f7;
  letter-spacing: -0.03em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 12px;
  text-align: ${({ $collapsed }) => $collapsed ? 'center' : 'left'};
  white-space: nowrap;
  overflow: hidden;
  transition: all 0.2s;
`;

const NavScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar { width: 0; }
`;

const hiddenText = css`
  opacity: 0;
  width: 0;
  overflow: hidden;
  white-space: nowrap;
`;

const visibleText = css`
  opacity: 1;
  width: auto;
  transition: opacity 0.15s 0.05s;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: ${({ $collapsed }) => $collapsed ? '14px 0 6px' : '14px 12px 6px'};
  text-align: ${({ $collapsed }) => $collapsed ? 'center' : 'left'};
  ${({ $collapsed }) => $collapsed && hiddenText}
  height: ${({ $collapsed }) => $collapsed ? '20px' : 'auto'};
  transition: all 0.2s;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => $collapsed ? '0' : '10px'};
  padding: 8px ${({ $collapsed }) => $collapsed ? '0' : '12px'};
  margin: 1px 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.85)'};
  background: ${({ $active }) => $active ? 'rgba(0, 122, 255, 0.25)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;
  justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
  position: relative;
  &:hover {
    background: ${({ $active }) => $active ? 'rgba(0, 122, 255, 0.3)' : 'rgba(255, 255, 255, 0.06)'};
    color: #f5f5f7;
  }
`;

const NavLabel = styled.span`
  ${({ $collapsed }) => $collapsed ? hiddenText : visibleText}
  white-space: nowrap;
`;

const NavIcon = styled.span`
  display: flex;
  font-size: 20px;
  min-width: 20px;
  color: ${({ $active }) => $active ? '#0a84ff' : 'rgba(255, 255, 255, 0.6)'};
`;

const Tooltip = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  background: #2c2c2e;
  color: #f5f5f7;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.12s;
  z-index: 1000;
  ${NavItem}:hover & {
    opacity: ${({ $show }) => $show ? 1 : 0};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 8px ${({ $collapsed }) => $collapsed ? '4px' : '12px'};
`;

const Bottom = styled.div`
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const LogoutBtn = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $collapsed }) => $collapsed ? '0' : '10px'};
  padding: 8px ${({ $collapsed }) => $collapsed ? '0' : '12px'};
  justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.12s;
  &:hover {
    background: rgba(255, 59, 48, 0.15);
    color: #ff453a;
  }
`;

const STORAGE_KEY_SIDEBAR = 'sidebar_collapsed';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const Sidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY_SIDEBAR) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  // 페이지 이동 시 모바일 사이드바 닫기
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [path, isMobile]);

  // 모바일에서 사이드바 열릴 때 스크롤 방지
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(STORAGE_KEY_SIDEBAR, next);
  };

  const isActive = (p) => path === p || path.startsWith(p + '/');
  const nav = (to) => () => navigate(to);
  const c = isMobile ? false : collapsed;

  const _handlePassword = async () => {
    const user = auth.currentUser;
    const { value: pwd } = await swal.fire({
      title: '새로운 비밀번호를 입력해 주세요.',
      text: '변경 후에는 새로운 비밀번호로 재로그인이 필요합니다.',
      input: 'password',
      inputPlaceholder: '6자리 이상 입력',
      confirmButtonText: '확인',
      cancelButtonText: '취소',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return '입력된 값이 없습니다.';
      },
    });
    if (pwd) {
      updatePassword(user, pwd)
        .then(() => {
          swal.fire({
            title: '변경이 완료되었습니다.',
            text: '재로그인을 위해 로그아웃됩니다.',
            icon: 'success',
            confirmButtonColor: '#007aff',
          }).then((result) => {
            if (result.isConfirmed) {
              localStorage.removeItem('user');
              window.location.reload();
            }
          });
        })
        .catch(() => {
          swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
        });
    }
  };

  const _handleShipping = async () => {
    const { data: prev } = await axios.get('/admin/shipping/price');
    const { value: shipping } = await swal.fire({
      title: '새로운 배송비값을 입력해 주세요.',
      text: `현재 배송비는 ${prev}원입니다.`,
      input: 'number',
      inputPlaceholder: '숫자만 입력',
      confirmButtonText: '확인',
      cancelButtonText: '취소',
      confirmButtonColor: '#007aff',
      cancelButtonColor: '#a3a3a3',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return '입력된 값이 없습니다.';
      },
    });
    if (shipping) {
      await axios.patch(`/admin/shipping/price/${shipping}`)
        .then(() => {
          swal.fire({ title: '변경 완료', text: '이후 주문건부터 적용됩니다.', icon: 'success', confirmButtonColor: '#007aff' });
        })
        .catch(() => {
          swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
        });
    }
  };

  const MenuItem = ({ icon, label, active, onClick }) => (
    <NavItem $active={active} $collapsed={c} onClick={onClick}>
      <NavIcon $active={active}>{icon}</NavIcon>
      <NavLabel $collapsed={c}>{label}</NavLabel>
      {c && <Tooltip $show={c}>{label}</Tooltip>}
    </NavItem>
  );

  // 외부에서 커스텀 이벤트로 사이드바 열기
  useEffect(() => {
    const handler = () => setMobileOpen(true);
    window.addEventListener('open-sidebar', handler);
    return () => window.removeEventListener('open-sidebar', handler);
  }, []);

  return (
    <>
    <MobileOverlay $open={mobileOpen} onClick={() => setMobileOpen(false)} />
    <Container $collapsed={c} $mobileOpen={mobileOpen}>
      <ToggleBtn onClick={toggle}>
        {c ? <BiChevronRight /> : <BiChevronLeft />}
      </ToggleBtn>

      <Logo $collapsed={c}>{c ? 'ST' : 'SUPERTRACK'}</Logo>

      <NavScroll>
        <SectionLabel $collapsed={c}>{c ? '' : '주문'}</SectionLabel>
        <MenuItem icon={<BiClipboard />} label="주문 목록" active={isActive('/order/list')} onClick={nav('/order/list')} />

        <SectionLabel $collapsed={c}>{c ? '' : '고객'}</SectionLabel>
        <MenuItem icon={<BiUserCircle />} label="회원 목록" active={isActive('/user/list')} onClick={nav('/user/list')} />
        <MenuItem icon={<BiBuildings />} label="회사 목록" active={isActive('/company')} onClick={nav('/company/list')} />

        <SectionLabel $collapsed={c}>{c ? '' : '제품'}</SectionLabel>
        <MenuItem icon={<BiBox />} label="제품 조회" active={path === '/product/list'} onClick={nav('/product/list')} />
        <MenuItem icon={<BiBox />} label="제품 등록" active={isActive('/product/add')} onClick={nav('/product/add')} />
        <MenuItem icon={<BiBox />} label="제품 분류" active={path === '/product/category'} onClick={nav('/product/category')} />
        <MenuItem icon={<BiBox />} label="전체 가격표" active={isActive('/product/total')} onClick={nav('/product/total')} />

        <SectionLabel $collapsed={c}>{c ? '' : '분석'}</SectionLabel>
        <MenuItem icon={<BiBarChartAlt2 />} label="판매 통계" active={isActive('/admin/stats')} onClick={nav('/admin/stats')} />

        <Divider $collapsed={c} />

        <SectionLabel $collapsed={c}>{c ? '' : '설정'}</SectionLabel>
        <MenuItem icon={<BiCog />} label="메인 이미지" active={isActive('/admin/main')} onClick={nav('/admin/main')} />
        <MenuItem icon={<BiCog />} label="공지 이미지" active={isActive('/admin/notice')} onClick={nav('/admin/notice')} />
        <MenuItem icon={<BiCog />} label="배송비 변경" onClick={_handleShipping} />
        <MenuItem icon={<BiCog />} label="비밀번호 변경" onClick={_handlePassword} />

        <Divider $collapsed={c} />

        <MenuItem icon={<BiTrash />} label="휴지통" active={isActive('/admin/trash')} onClick={nav('/admin/trash')} />
      </NavScroll>

      <Bottom>
        <LogoutBtn $collapsed={c} onClick={onLogout}>
          <BiLogOut size={18} />
          <NavLabel $collapsed={c}>로그아웃</NavLabel>
        </LogoutBtn>
      </Bottom>
    </Container>
    </>
  );
};

export default Sidebar;
